import { useEffect, useState } from "react";
import { ArrowUpRight, PanelsTopLeft } from "lucide-react";
import type { BlogIndex, BlogSettings, BuilderApi } from "../api/contract";
import { BlogRefusedError } from "../api/errors";
import { AreaError, Card, Field, ListHead, SaveBar, SwitchRow } from "./area";
import { BlogLoadingStatus } from "./BlogLoadingStatus";
import { Spinner } from "./Spinner";
import { requestTimeout } from "../lib/requestTimeout";

const pathOf = (base: string) => base.replace(/^\//, "");
const validPath = (path: string) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(path);

interface BlogSettingsViewProps {
  api: BuilderApi;
  siteId: string;
  blog: BlogIndex;
  refreshing?: boolean;
  /** The site's address without the scheme, for the web address field. */
  address: string;
  reload: () => Promise<void>;
  /** The blog's pages are designed in the Website, with Stax. */
  onDesignPages: () => void;
}

/**
 * The blog's own settings. Like every blog setting they are live as soon as they save: there is no Publish to wait for.
 * Undo saves what the settings were before.
 */
export function BlogSettingsView({ api, siteId, blog, refreshing = false, address, reload, onDesignPages }: BlogSettingsViewProps) {
  const settings = blog.settings!;
  const [name, setName] = useState(settings.name);
  const [path, setPath] = useState(pathOf(settings.base));
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<BlogSettings | null>(null);
  const [turningOff, setTurningOff] = useState(false);
  useEffect(() => { setName(settings.name); setPath(pathOf(settings.base)); }, [settings.name, settings.base]);
  const host = address.replace(/\/$/, "");

  /** Saves a change and keeps what it replaced, for Undo. */
  const save = async (change: Partial<BlogSettings>, undoing = false): Promise<boolean> => {
    if (busy) return false;
    setBusy(true); setToggling(change.enabled !== undefined && change.enabled !== settings.enabled); setError(null);
    const before = settings;
    try {
      const next = { ...settings, ...change };
      await requestTimeout(() => api.saveBlogSettings(siteId, { name: next.name, base: next.base, enabled: next.enabled, addToMenu: next.addToMenu, latestOnHome: next.latestOnHome }), undefined, 60_000);
      await reload();
      setSaved(undoing ? null : before);
      return true;
    } catch (caught) {
      setError(caught instanceof BlogRefusedError ? caught.message : "That didn't save. Try again.");
      setName(settings.name); setPath(pathOf(settings.base));
      return false;
    } finally { setBusy(false); setToggling(false); }
  };
  const commitName = () => { const next = name.trim(); if (next && next !== settings.name) void save({ name: next }); else setName(settings.name); };
  const commitPath = () => { if (validPath(path) && `/${path}` !== settings.base) void save({ base: `/${path}` }); else setPath(pathOf(settings.base)); };

  return <div className="bw-blog-settings-view" aria-busy={busy}>
      <ListHead title="Settings" status={<BlogLoadingStatus busy={refreshing && !busy} />} />
      {!settings.enabled && <div className="bw-blog-off" role="status">
        <div><strong>The blog is off</strong><p>Its pages and menu link are hidden. Your posts are kept.</p></div>
        <button type="button" className="bw-btn bw-btn--primary" disabled={busy} onClick={() => void save({ enabled: true })}>{toggling && <Spinner />}Turn on</button>
      </div>}
      <Card>
        <Field label="Blog name">
          <input value={name} maxLength={80} disabled={busy} onChange={(event) => setName(event.target.value)} onBlur={commitName} onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }} />
        </Field>
        <Field label="Web address" hint="Old addresses keep working and send visitors here.">
          <span className="bw-area-prefixed"><span>{host ? `${host}/` : "/"}</span><input aria-label="Blog web address" value={path} disabled={busy} aria-invalid={!validPath(path) || undefined} onChange={(event) => setPath(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/^-+/, "").slice(0, 40))} onBlur={commitPath} onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }} /></span>
        </Field>
      </Card>
      <Card title="Where the blog shows up" description="Like every blog setting, these are live as soon as you change them. No website Publish needed.">
        <SwitchRow title="Link in the menu" hint={`“${settings.name}” in your site's menu`} checked={settings.addToMenu} disabled={busy || !settings.enabled} onChange={(next) => void save({ addToMenu: next })} />
        <SwitchRow title="Latest posts on Home" hint="The 3 newest published posts" checked={settings.latestOnHome} disabled={busy || !settings.enabled} onChange={(next) => void save({ latestOnHome: next })} />
      </Card>
      <button type="button" className="bw-area-card bw-blog-design-card" onClick={onDesignPages}>
        <span className="bw-blog-design-icon"><PanelsTopLeft size={18} aria-hidden="true" /></span>
        <span><strong>Design blog pages</strong><small>The {settings.name} page, post page and tag pages are designed in Website, with Stax.</small></span>
        <span className="bw-blog-design-open">Open in Website <ArrowUpRight size={14} aria-hidden="true" /></span>
      </button>
      {settings.enabled && <section className="bw-area-card bw-blog-turn-off">
        {turningOff ? <div className="bw-blog-delete" role="group" aria-label="Confirm">
          <p>Turn off the blog? Its pages and menu link leave your site at once. Your posts are kept, and come back when you turn it on.</p>
          <div className="bw-actions">
            <button type="button" className="bw-btn bw-btn--quiet" disabled={busy} onClick={() => setTurningOff(false)}>Keep it on</button>
            <button type="button" className="bw-btn bw-btn--danger" disabled={busy} onClick={async () => { if (await save({ enabled: false })) setTurningOff(false); }}>{toggling && <Spinner />}Turn off</button>
          </div>
        </div> : <>
          <span><strong>Turn off the blog</strong><small>Hides its pages and menu link. Your posts are kept.</small></span>
          <button type="button" className="bw-btn bw-btn--quiet" disabled={busy} onClick={() => setTurningOff(true)}>Turn off…</button>
        </>}
      </section>}
      {error && <AreaError message={error} onDismiss={() => setError(null)} />}
      {(saved || busy) && <SaveBar state="saved" busy={busy} onUndo={saved ? async () => { if (await save({ ...saved }, true)) setSaved(null); } : undefined} />}
  </div>;
}
